import cx from 'classnames';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import warningIcon from '../../assets/svg/warning-icon.svg';
import { OnboardingContext } from '../../context/OnboardingContext';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import InfoModal from '../../ui/Modal/InfoModal';
import { SpacerHr } from '../../ui/Styled';
import StyledCheckBox from '../../ui/Styled/StyledCheckbox';
import { max, min } from '../../utils/breakpoints';

import SmartTariffInfoContent from './SmartTariffInfoContent';

interface FormValues {
  confirm: boolean;
}

const StyledButton = styled(Button)`
  margin-top: 36px;
  text-decoration: none !important;
`;

const StyledConfirmation = styled.div`
  margin-left: 32px;
  font-size: 14px;

  ${min.sm} {
    font-size: 16px;
  }
`;

const StyledImg = styled.img`
  ${max.sm} {
    width: 100%;
    max-width: 60px;
  }
`;

const SmartTariffWarningContainer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [touched, setTouched] = useState<boolean>(false);
  const { setProgressPercentage } = useContext(OnboardingContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const validationSchema = Yup.object({
    confirm: Yup.boolean().required().oneOf([true]),
  });

  const onSubmit = (): void => {
    void navigate('/onboarding/pv-system');
  };

  useEffect(() => {
    setProgressPercentage(15);
  }, []);

  return (
    <>
      <div className="mb-3">
        <StyledImg src={warningIcon} alt="Warning icon" />
      </div>
      <h1 className="mb-3">Warning!</h1>
      <div>
        <p>
          <strong>{theme.language.appName} won't work if you're not on a smart tariff</strong>
        </p>
        <p>
          You can continue signing up, then once we have all your data, we'll recommend which smart tariff combination
          you should be on.
        </p>
        <div className="text-left mt-4">
          <Formik<FormValues>
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={{
              confirm: false,
            }}
            validateOnChange
          >
            {({ handleSubmit, isValid, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                {!isSubmitting ? (
                  <>
                    <Field name="confirm">
                      {({ field, meta }: any) => (
                        <StyledCheckBox
                          className={`text-left ${cx({
                            selected: field.value,
                            error: !!meta.error,
                          })}`}
                        >
                          <Form.Check
                            type="checkbox"
                            name="confirm"
                            id="confirm-check"
                            onChange={(e: React.ChangeEvent) => {
                              setTouched(true);
                              return field.onChange(e);
                            }}
                            onBlur={field.onBlur}
                            className={cx({ error: !!meta.error })}
                            label={
                              <StyledConfirmation>
                                I understand that {theme.language.appName} will not make changes to my hardware until I
                                have switched to a smart tariff.
                              </StyledConfirmation>
                            }
                          />
                          {meta.error && meta.touched && <ErrorLabel pad={false} showIcon={false} label={meta.error} />}
                        </StyledCheckBox>
                      )}
                    </Field>
                    <Button
                      variant="primary"
                      disabled={!isValid || isSubmitting || !touched}
                      className="w-100"
                      type="submit"
                    >
                      Continue using {theme.language.appName}
                    </Button>
                    <SpacerHr label="or" />
                    <StyledButton variant="secondary" className="w-100" onClick={() => setShowInfoModal(true)}>
                      Find out more about smart tariffs
                    </StyledButton>
                  </>
                ) : (
                  <LoopSpinner />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <InfoModal
        size="lg"
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        infoContent={<SmartTariffInfoContent />}
      />
    </>
  );
};

export default SmartTariffWarningContainer;
